import type { BigNumber } from 'bignumber.js';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { WebsiteSupportedChains } from '@/lib/constants';
import { normalizeReceiverAddress } from '@/lib/utils';
import { useBalance } from './use-balance';

interface UseTransferValidationParams {
  fromAmountBN: BigNumber;
  receiver: string;
  minAmount?: number;
  maxAmount?: number;
  isRouteInfoFetching: boolean;
  fromAddress?: string;
  toChain: WebsiteSupportedChains;
  fromChain: WebsiteSupportedChains;
}

export function useTransferValidation({
  fromAmountBN,
  receiver,
  minAmount,
  maxAmount,
  isRouteInfoFetching,
  fromAddress,
  toChain,
  fromChain,
}: UseTransferValidationParams) {
  const { t } = useTranslation();
  const { data: balance = 0 } = useBalance(fromChain, fromAddress, !!fromAddress);
  const error = useMemo(() => {
    if (isRouteInfoFetching) {
      return t('validation.fetchingRouteInfo');
    }

    if (!fromAddress) {
      return t('validation.pleaseConnectWallet');
    }

    if (fromAmountBN.isLessThan(minAmount ?? 0)) {
      return t('validation.amountTooLow');
    }

    if (fromAmountBN.isGreaterThan(maxAmount ?? 0)) {
      return t('validation.amountTooHigh');
    }

    if (fromAmountBN.isGreaterThan(balance)) {
      return t('validation.insufficientBalance');
    }

    if (receiver === '') {
      return t('validation.receiverAddressRequired');
    }

    try {
      normalizeReceiverAddress(toChain, receiver);
    } catch (error) {
      if (error instanceof Error) {
        // Map error messages to translation keys
        const errorMessage = error.message;
        if (errorMessage.includes('D9 address')) {
          return t('validation.receiverAddressInvalidD9');
        }
        if (errorMessage.includes('EVM address')) {
          return t('validation.receiverAddressInvalidEVM');
        }
        if (errorMessage.includes('Tron address')) {
          return t('validation.receiverAddressInvalidTron');
        }
        if (errorMessage.includes('Invalid D9 address')) {
          return t('validation.invalidD9Address');
        }
        if (errorMessage.includes('Unsupported chain')) {
          return t('errors.unsupportedChain');
        }
        return errorMessage;
      }
      return t('validation.receiverAddressInvalid');
    }

    return null;
  }, [
    fromAmountBN,
    minAmount,
    maxAmount,
    balance,
    receiver,
    isRouteInfoFetching,
    fromAddress,
    toChain,
    fromChain,
    t,
  ]);

  return { error };
}


